import { Box, Button, Divider, Menu, MenuItem, Typography } from '@mui/material'
import { MouseEvent, useContext, useState } from 'react'

import { PauseCircleOutlineOutlined, PlayCircleFilledWhiteOutlined } from '@mui/icons-material'
import { EntryType } from '../../../generated'
import { PlusIcon } from '../../assets/images'
import { AppContext, AuthContext } from '../../context'
import { TimerButtonProps } from '../../interfaceTypes/props'
import palette from '../../theme/palette'
import { resetEntryType } from '../../utils'

import { ActionType } from '../../context/AppContextReducer'
import { useTimer } from '../../context/TimerContext'
import { MenuItems } from '../../interfaceTypes/common'
import ManualEntry from './ManualEntry'

const TimerDisplay = () => {
  const { elapsedTime } = useTimer()

  const formatTime = (milliseconds: number) => {
    const totalSeconds = Math.floor(milliseconds / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`
  }

  return (
    <div>
      <Typography variant="button" paddingX={1}>
        {formatTime(elapsedTime)}
      </Typography>
    </div>
  )
}

const TimerButton = ({ handleOpenDialog, isPaused, pauseOrResumeTimer, elapsedTime, startTime }: TimerButtonProps) => {
  const [activeHover, setActiveHover] = useState<string | null>(null)
  const [createAnchorEl, setCreateAnchorEl] = useState<null | HTMLElement>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { dispatch, entryType } = useContext(AppContext)
  const { isInvestigator, isAttorney } = useContext(AuthContext)

  const open = Boolean(createAnchorEl)

  const Menu_Items: MenuItems[] = [
    {
      label: 'Time Entry',
      actionType: ActionType.SET_TIME_ENTRY,
      type: EntryType.Time,
      visible: !isInvestigator
    },
    {
      label: 'Cost Entry',
      actionType: ActionType.SET_COST_ENTRY,
      type: EntryType.Cost,
      visible: !isAttorney
    },
    {
      label: 'Legal Fee',
      actionType: ActionType.SET_LEGAL_FEE,
      type: EntryType.Legal,
      visible: true
    },
    {
      label: 'Expense Entry',
      actionType: ActionType.SET_IS_EXPENSE_ENTRY,
      type: EntryType.Expense,
      visible: true
    },
    {
      label: 'Non Client Entry',
      actionType: ActionType.SET_NON_CLIENT_ENTRY,
      type: EntryType.NonClient,
      visible: true
    }
  ]

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setCreateAnchorEl(event.currentTarget)
  }

  const closeMenu = (event: React.MouseEvent<HTMLLIElement>) => {
    setCreateAnchorEl(null)
    event.stopPropagation()
  }

  const openModal = (event: React.MouseEvent<HTMLLIElement>) => {
    closeMenu(event)
    handleOpen()
    resetEntryType(dispatch)
  }
  const handleOpen = () => setIsModalOpen(true)

  return (
    <>
      <Button
        variant="contained"
        onClick={() => {
          resetEntryType(dispatch)
          dispatch({ type: ActionType.SET_TIME_ENTRY, value: true })
          return handleOpenDialog?.()
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: 0,
          height: '40px',
          '&:hover': activeHover ? { boxShadow: '1px', backgroundColor: palette.primary.main } : {}
        }}
      >
        <Box
          display={'flex'}
          p={1}
          onMouseEnter={() => setActiveHover('playPause')}
          onMouseLeave={() => setActiveHover(null)}
          sx={{ '&:hover': activeHover === 'playPause' ? { opacity: 0.8 } : {} }}
        >
          <Box
            onClick={event => {
              // Stop the bubble so the outer <Button> does not also open the
              // dialog on top of the toggle.
              event.stopPropagation()
              // With no timer yet the icon reads as "start", and the only way
              // to start one is through the dialog. Previously this returned
              // early *after* stopping propagation, so clicking play did
              // nothing at all.
              return startTime ? pauseOrResumeTimer?.() : handleOpenDialog?.()
            }}
            height={24}
            sx={{
              '&:hover': { color: 'red' }
            }}
          >
            {startTime && !isPaused ? <PauseCircleOutlineOutlined /> : <PlayCircleFilledWhiteOutlined />}
          </Box>

          <TimerDisplay />
        </Box>

        <Divider orientation="vertical" sx={{ borderColor: 'white', height: '40px' }} />

        <Box
          component="button"
          onMouseEnter={() => setActiveHover('1')}
          onMouseLeave={() => setActiveHover(null)}
          sx={{
            '&:hover': activeHover === '1' ? { opacity: 0.8 } : {},
            all: 'unset',
            cursor: 'pointer',
            padding: 2
          }}
          aria-controls={open ? 'create-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
        >
          <PlusIcon />
        </Box>

        <Menu
          id="create-menu"
          anchorEl={createAnchorEl}
          open={open}
          onClose={closeMenu}
          MenuListProps={{ 'aria-labelledby': 'basic-button' }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ '& .MuiPaper-root': { marginLeft: 5 } }}
        >
          {Menu_Items.map(
            item =>
              item.visible && (
                <MenuItem
                  onClick={event => {
                    openModal(event)
                    dispatch({ type: item.actionType, value: true })
                    dispatch({ type: ActionType.SET_ENTRY_TYPE, entryType: item.type || entryType })
                  }}
                >
                  {item.label}
                </MenuItem>
              )
          )}
        </Menu>
      </Button>

      <ManualEntry isOpen={isModalOpen} handleClose={() => setIsModalOpen(false)} />
    </>
  )
}

export default TimerButton
